import { useEffect, useRef, useState } from "react";
import { WorkshopWrapper } from "./Styled";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  Box,
  ButtonStyled,
  HeadingStyled,
  SpanStyled,
} from "../../components/Styled/Elements";
import { ButtonGroup } from "./Styled";
import { PreviousButton } from "./Styled";
import { NextButton } from "./Styled";
import { SubmitButton } from "./Styled";
import WorkshopHeader from "../../components/header/WorkshopHeader";
import WorkshopSidebar from "../../components/sidebar/WorkshopSidebar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";

const Workshop = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const [sidebarStatus, setSidebarStatus] = useState(false);
  const [tutorialsData, setTutorialsData] = useState([]);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [participant, setParticipant] = useState(null);
  const [watchedCount, setWatchedCount] = useState(0);
  const [isGenerateCertificateLoading, setIsGenerateCertificateLoading] = useState(false);

  const playerRef = useRef(null);
  const DESCRIPTION_LIMIT = 30;

  // Global extraction logic matching backend capability
  const extractYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i);
    return match ? match[1] : null;
  };

  // Helper to truncate description
  const getTruncatedDescription = (desc) => {
    const words = desc?.split(" ");
    if (!words || words.length <= DESCRIPTION_LIMIT) return desc;
    return words.slice(0, DESCRIPTION_LIMIT).join(" ") + "...";
  };

  const progressPercentage =
    tutorialsData.length > 0
      ? Math.round((watchedCount / tutorialsData.length) * 100)
      : 0;

  // Fetch participant data
  useEffect(() => {
    const storedParticipant = localStorage.getItem("participant_data");
    if (!storedParticipant) {
      navigate("/");
      return;
    }
    const parsed = JSON.parse(storedParticipant);
    setParticipant(parsed);
    setWatchedCount(parsed?.watched_videos_counter || 0);
  }, [navigate]);

  // Fetch workshop videos
  const fetchTutorialsData = async () => {
    try {
      const res = await fetch(`${API_URL}/workshop-videos`, {
        headers: { "X-localization": language },
      });
      const data = await res.json();
      if (res.ok) {
        setTutorialsData(data.data || []);
        if (data.data?.length > 0) setSelectedTutorial(data.data[0]);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchTutorialsData();
  }, [language]);

  const currentIndex = tutorialsData.findIndex(
    (t) => t.id === selectedTutorial?.id
  );

  // Previous button
  const handlePrevious = () => {
    if (currentIndex <= 0) return;
    const prevIndex = currentIndex - 1;
    if (prevIndex > watchedCount) return; // only allow already watched
    setSelectedTutorial(tutorialsData[prevIndex]);
    setShowFullDesc(false);
    setSidebarStatus(false);
  };

  // Next button
  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= tutorialsData.length) return;
    if (watchedCount <= currentIndex) return; // must watch current first
    setSelectedTutorial(tutorialsData[nextIndex]);
    setShowFullDesc(false);
    setSidebarStatus(false);
  };

  const handleSubmit = async() => {
    setIsGenerateCertificateLoading(true);
    try {
      const response = await axios.post(`${API_URL}/workshop-videos/request-certificate`, {
        participant_id: participant?.id,
      });

      const participantData = response.data.data;

      // Update local storage
      const updatedParticipant = {
        ...participant,
        certificate_status: participantData?.certificate_status || "pending",
      };
      localStorage.setItem(
        "participant_data",
        JSON.stringify(updatedParticipant)
      );
      setWatchedCount(updatedParticipant.watched_videos_counter);
      setParticipant(updatedParticipant);
      console.log('response: ', response);

      if(response.data.code >= 400) {
        toast.success(response.data.error);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast.error(error.data.message || error.error);
    } finally {
      setIsGenerateCertificateLoading(false);
    }
  };

  // Video completed logic
  const handleVideoCompleted = async () => {
    if (!selectedTutorial || currentIndex < watchedCount) return; // already watched
    try {
      // API call
      await axios.post(`${API_URL}/workshop-videos/watched-counter`, {
        participant_id: participant?.id,
      });

      // Update local storage
      const updatedParticipant = {
        ...participant,
        watched_videos_counter: (participant?.watched_videos_counter || 0) + 1,
      };
      localStorage.setItem(
        "participant_data",
        JSON.stringify(updatedParticipant)
      );
      setWatchedCount(updatedParticipant.watched_videos_counter);
      setParticipant(updatedParticipant);
      console.log("Video completed and counter updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update video counter");
    }
  };

  // YouTube player state change
  const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.ENDED) {
      handleVideoCompleted();
    }
  };

  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  }, []);

  // Initialize or update YouTube player with nocookie parameters
  useEffect(() => {
    if (!window.YT || !selectedTutorial) return;

    console.log("selectedTutorial: ", selectedTutorial);

    // FIX 1: Robust extraction via regular expressions
    const videoId = extractYoutubeId(selectedTutorial.attachment);

    if (!videoId) {
      console.error("Invalid YouTube URL encountered:", selectedTutorial.attachment);
      return;
    }

    // Destroy old player instance securely
    if (playerRef.current) {
      playerRef.current.destroy();
    }

    // Create new player with advanced environment parameters
    // FIX 2: host parameter targets the privacy-enhanced, cross-origin container
    playerRef.current = new window.YT.Player("yt-player", {
      host: "https://www.youtube-nocookie.com",
      videoId: videoId,
      playerVars: {
        rel: 0,
        modestbranding: 1
      },
      events: { onStateChange: onPlayerStateChange },
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [selectedTutorial]);

  return (
    <WorkshopWrapper className="d-flex">
      <WorkshopHeader
        sidebarStatus={sidebarStatus}
        setSidebarStatus={setSidebarStatus}
      />
      <WorkshopSidebar
        data={tutorialsData}
        selectedTutorial={selectedTutorial}
        onSelect={setSelectedTutorial}
        sidebarStatus={sidebarStatus}
        setSidebarStatus={setSidebarStatus}
        participant={participant}
      />

      {tutorialsData.length === 0 ? (
        <Box className="flex-grow-1 custom-scroll content-main center-main">
          <Box className="py-3 px-4" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            No Record Found
          </Box>
        </Box>
      ) : (
      <Box className="flex-grow-1 custom-scroll content-main center-main">
        <Box className="pg-title">
          <Box className="py-3 px-4">
            <HeadingStyled size="18px" color="var(--text)" weight="bold">
              {t("workshop_videos")}
            </HeadingStyled>
          </Box>

          <div className="w-100">
            <div
              className="progress position-relative"
              style={{ height: "14px" }}
            >
              {/* Progress Fill */}
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${progressPercentage}%`,
                  backgroundColor: "#38D200",
                }}
                aria-valuenow={progressPercentage}
                aria-valuemin="0"
                aria-valuemax="100"
              />

              {/* Centered Percentage Text */}
              <span
                className="position-absolute top-50 start-50 translate-middle fw-semibold"
                style={{
                  fontSize: "11px",
                  color: progressPercentage > 50 ? "#fff" : "#000",
                  pointerEvents: "none",
                }}
              >
                {progressPercentage}%
              </span>
            </div>
          </div>
        </Box>

        <Box className="p-3">
          {selectedTutorial && (
            <Box className="content-box py-3 px-4">
              <HeadingStyled size="36px" color="var(--text)">
                {selectedTutorial.title}
              </HeadingStyled>

              <Box className="pb-3">
                <SpanStyled>
                  {selectedTutorial.description && showFullDesc
                    ? selectedTutorial.description
                    : getTruncatedDescription(selectedTutorial.description)}
                  {selectedTutorial.description?.split(" ").length >
                    DESCRIPTION_LIMIT && (
                    <ButtonStyled
                      width="auto"
                      className="py-0 px-2"
                      color="var(--themeColor)"
                      onClick={() => setShowFullDesc((prev) => !prev)}
                    >
                      {showFullDesc ? t("show_less") : t("show_more")}
                    </ButtonStyled>
                  )}
                </SpanStyled>
              </Box>

              {/* YouTube player mount node */}
              <Box className="malik">
                <div id="yt-player" className="tutorial-vid"></div>
              </Box>

              <Box className="d-flex align-items-center justify-content-end mt-3">
                <ButtonGroup>
                  {currentIndex > 0 && (
                    <PreviousButton onClick={handlePrevious}>
                      {t("previous")}
                    </PreviousButton>
                  )}

                  <NextButton
                    onClick={handleNext}
                    disabled={
                      currentIndex === tutorialsData.length - 1 ||
                      watchedCount <= currentIndex
                    }
                  >
                    {t("next")}
                  </NextButton>

                  {participant &&
                    tutorialsData.length > 0 &&
                    watchedCount / tutorialsData.length >= 0.6 && (
                      <SubmitButton onClick={handleSubmit}>
                        {isGenerateCertificateLoading && (
                          <Spinner variant="success" />
                        )}
                        {t("request_certificate")}
                      </SubmitButton>
                    )}
                </ButtonGroup>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      )}
    </WorkshopWrapper>
  );
};

export default Workshop;